import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import { getContractDocuments } from '../../../services/NewServicesCompnay'
import {
  CONTRACT_PROFILE_ID,
  filterContractDocumentsByProfile,
} from '../../../utils/contractDocuments'

dayjs.locale('es')

// ─── Mapeo de documentos / contratos ──────────────────────────────────────────
// document_id → etiqueta legible (mismo mapeo usado en el resto de la app).
const CONTRACT_LABELS = {
  1: 'Hutrit Club',
  2: 'Top 10',
  3: 'Talento A',
}

// Colores asignados de forma estable por document_id (con fallback por índice).
const DOC_COLORS = {
  1: '#7c3aed',
  2: '#0891b2',
  3: '#059669',
}
const FALLBACK_COLORS = ['#d97706', '#db2777', '#2563eb', '#4f46e5']

// ─── Helpers de contrato ──────────────────────────────────────────────────────

const parseBoolText = (value) => String(value).toLowerCase() === 'true'

const getDocumentId = (doc) => Number(doc?.id) || null

export const getContractLabel = (doc) =>
  doc?.name || CONTRACT_LABELS[getDocumentId(doc)] || 'Contrato de servicios'

const getContractColor = (doc, index = 0) => {
  const docId = getDocumentId(doc)
  return DOC_COLORS[docId] || FALLBACK_COLORS[index % FALLBACK_COLORS.length]
}

/** Tarifa mensual base (sin impuesto) tomada del documento de contrato. */
const getContractRate = (doc) => Number(doc?.extra?.amount) || 0

/** Si el documento aplica impuesto (el backend lo guarda como texto). */
const contractTaxApplies = (doc) => parseBoolText(doc?.extra?.taxApplies)

/** Porcentaje de impuesto del documento. */
const contractTaxPercent = (doc) => Number(doc?.extra?.taxes) || 0

/** Multiplicador de impuesto derivado del propio documento. */
const getContractTaxMultiplier = (doc) => {
  if (!contractTaxApplies(doc)) return 1
  return 1 + contractTaxPercent(doc) / 100
}

/**
 * Calcula los días restantes del mes en base comercial de 30 días,
 * incluyendo el día actual dentro del rango a facturar.
 * Día 1 → 30 días completos. Día 14 → 30 - 14 + 1 = 17 días.
 */
export const getDaysRemaining = (dateStr) => {
  if (!dateStr) return null
  const day = dayjs(dateStr).date()
  return 30 - day + 1
}

const fmt = (n) =>
  new Intl.NumberFormat('es-ES', {
    style:                 'currency',
    currency:              'EUR',
    minimumFractionDigits: 2,
  }).format(n)

// ─── Sub-componentes internos ─────────────────────────────────────────────────

const Label = ({ children }) => (
  <p
    className="mb-2"
    style={{
      fontSize:      '11px',
      fontWeight:    '700',
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      color:         '#6b7280',
    }}
  >
    {children}
  </p>
)

const ReadOnlyBadge = ({ label, value }) => (
  <div className="d-flex flex-column gap-1">
    <Label>{label}</Label>
    <div
      className="px-3 py-2 rounded d-flex align-items-center gap-2"
      style={{
        background:  '#f3f4f6',
        border:      '1px solid #e5e7eb',
        fontSize:    '13px',
        fontFamily:  'monospace',
        color:       '#374151',
        minHeight:   '38px',
      }}
    >
      <span
        style={{
          width:        '6px',
          height:       '6px',
          borderRadius: '50%',
          background:   '#9ca3af',
          flexShrink:   0,
        }}
      />
      {value || '—'}
    </div>
  </div>
)

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * BillingCalculator
 *
 * Lista los documentos de contrato disponibles (getContractDocuments) y
 * calcula el cobro prorrateado del primer mes a partir de la tarifa, el impuesto
 * y la fecha de inicio del documento seleccionado.
 *
 * Props:
 *   companyId  — ID de la empresa (mostrado como solo lectura)
 *   createdAt  — ISO string de fecha de creación
 *   onChange   — (payload) => void  con el resumen del cálculo
 */
const BillingCalculator = ({ companyId, createdAt, onChange }) => {
  const [contractId, setContractId] = useState(null)
  const [startDate,  setStartDate]  = useState('')

  const { data: contractsData, isLoading } = useQuery({
    queryKey: ['contract-documents'],
    queryFn:  getContractDocuments,
  })

  // Solo documentos de contrato de empresa: se excluyen los de talento.
  const contracts = filterContractDocumentsByProfile(
    contractsData?.res?.documents,
    CONTRACT_PROFILE_ID.COMPANY,
  )

  const selectedContract = useMemo(
    () => contracts.find((c) => c?.id === contractId) ?? null,
    [contracts, contractId],
  )

  const activeColor = selectedContract
    ? getContractColor(selectedContract, contracts.indexOf(selectedContract))
    : '#4f46e5'

  // ── Cálculo reactivo ───────────────────────────────────────────────────────
  const calc = useMemo(() => {
    if (!selectedContract || !startDate) return null

    const rate   = getContractRate(selectedContract)
    const days   = getDaysRemaining(startDate)
    const mult   = getContractTaxMultiplier(selectedContract)
    const base   = parseFloat(((rate / 30) * days).toFixed(2))
    const taxAmt = parseFloat((base * (mult - 1)).toFixed(2))
    const total  = parseFloat((base * mult).toFixed(2))
    const fullMonth = parseFloat((rate * mult).toFixed(2))

    return { rate, days, base, taxAmt, total, fullMonth, mult }
  }, [selectedContract, startDate])

  const emit = (contract, nextDate) => {
    if (!onChange || !contract || !nextDate) return
    const rate = getContractRate(contract)
    const days = getDaysRemaining(nextDate)
    const mult = getContractTaxMultiplier(contract)
    if (!days) return
    const base = parseFloat(((rate / 30) * days).toFixed(2))
    onChange({
      contractId:  contract.id,
      tag:         getContractLabel(contract),
      taxApplies:  contractTaxApplies(contract),
      taxPercent:  contractTaxPercent(contract),
      startDate:   nextDate,
      baseAmount:  base,
      taxAmount:   parseFloat((base * (mult - 1)).toFixed(2)),
      total:       parseFloat((base * mult).toFixed(2)),
    })
  }

  const handleSelectContract = (contract) => {
    setContractId(contract.id)
    // Los documentos no traen fecha de inicio: se mantiene la elegida manualmente.
    emit(contract, startDate)
  }

  const handleDate = (e) => {
    setStartDate(e.target.value)
    emit(selectedContract, e.target.value)
  }

  const taxApplies = contractTaxApplies(selectedContract)
  const taxPercent = contractTaxPercent(selectedContract)

  return (
    <div className="d-flex flex-column gap-4">

      {/* ── 1. SELECTOR DE CONTRATO REAL ──────────────────────────────────── */}
      <div>
        <Label>Documento / contrato</Label>

        {isLoading ? (
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '13px', color: '#6b7280' }}>
            <span className="spinner-border spinner-border-sm" role="status" />
            Cargando contratos…
          </div>
        ) : !contracts.length ? (
          <div
            className="rounded p-3"
            style={{ background: '#fafafa', border: '1px dashed #e5e7eb', fontSize: '13px', color: '#9ca3af' }}
          >
            No hay contratos disponibles para esta empresa.
          </div>
        ) : (
          <div className="row g-2">
            {contracts.map((contract, index) => {
              const active   = contract.id === contractId
              const color    = getContractColor(contract, index)
              const label    = getContractLabel(contract)
              const status   = contract.active ? 'Activo' : 'Inactivo'
              const rate     = getContractRate(contract)
              const applies  = contractTaxApplies(contract)
              const percent  = contractTaxPercent(contract)
              return (
                <div key={contract.id} className="col-12 col-md-6 col-xl-4">
                  <button
                    type="button"
                    onClick={() => handleSelectContract(contract)}
                    className="d-flex align-items-center gap-3 text-start w-100 h-100"
                    style={{
                      padding:      '12px 16px',
                      borderRadius: '10px',
                      border:       `2px solid ${active ? color : '#e5e7eb'}`,
                      background:   active ? `${color}0d` : '#fff',
                      cursor:       'pointer',
                      transition:   'all 0.15s ease',
                      boxShadow:    active ? `0 2px 10px ${color}33` : 'none',
                    }}
                  >
                    <span
                      style={{
                        width:        '10px',
                        height:       '10px',
                        borderRadius: '50%',
                        background:   active ? color : '#d1d5db',
                        flexShrink:   0,
                      }}
                    />
                    <span className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: '#111827' }}>
                        {label}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {status || 'Contrato'}
                      </span>
                    </span>
                    <span className="text-end flex-shrink-0">
                      <span style={{ fontWeight: '700', fontSize: '13px', color }}>
                        {fmt(rate)}/mes
                      </span>
                      <span className="d-block" style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {applies ? `IVA ${percent}%` : 'No sujeto'}
                      </span>
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {selectedContract && (
          <p className="mt-2 mb-0" style={{ fontSize: '12px', color: '#6b7280' }}>
            Tarifa mensual base:{' '}
            <strong style={{ color: activeColor }}>
              {fmt(getContractRate(selectedContract))}/mes
            </strong>
            {taxApplies ? ` · IVA ${taxPercent}% aplicado` : ' · No sujeto a impuesto'}
          </p>
        )}
      </div>

      {/* ── GRID: inputs izq · resultado der ──────────────────────────────── */}
      <div className="row g-4">

        {/* Columna izquierda */}
        <div className="col-12 col-lg-5 d-flex flex-column gap-4">

          {/* Fecha */}
          <div>
            <Label>Fecha de inicio del servicio</Label>
            <input
              type="date"
              value={startDate}
              onChange={handleDate}
              className="form-control"
              style={{ maxWidth: '200px', fontSize: '14px' }}
            />
            {calc && (
              <p className="mt-2 mb-0" style={{ fontSize: '12px', color: '#6b7280' }}>
                Días a facturar:{' '}
                <strong style={{ color: '#111827' }}>{calc.days} días</strong>
                {' '}(base 30 días)
              </p>
            )}
          </div>

          {/* Impuesto (derivado del contrato) */}
          <div>
            <Label>Impuesto</Label>
            <div
              className="px-3 py-2 rounded d-flex align-items-center gap-2"
              style={{
                background: '#f3f4f6',
                border:     '1px solid #e5e7eb',
                fontSize:   '13px',
                color:      '#374151',
                minHeight:  '38px',
              }}
            >
              {!selectedContract
                ? '— Selecciona un contrato'
                : taxApplies
                  ? `IVA ${taxPercent}% (según contrato)`
                  : 'No sujeto (según contrato)'}
            </div>
          </div>

          {/* Campos solo lectura */}
          <div className="d-flex flex-column gap-3">
            <Label>Información del registro</Label>
            <ReadOnlyBadge
              label="ID de empresa"
              value={companyId ? `#${companyId}` : null}
            />
            <ReadOnlyBadge
              label="Fecha de alta"
              value={createdAt ? dayjs(createdAt).format('DD/MM/YYYY HH:mm') : null}
            />
          </div>

        </div>

        {/* Columna derecha: resultado */}
        <div className="col-12 col-lg-7">
          <Label>Resultado del cálculo</Label>

          {!calc ? (
            <div
              className="rounded d-flex flex-column justify-content-center align-items-center text-center gap-3"
              style={{
                minHeight:  '260px',
                background: '#fafafa',
                border:     '2px dashed #e5e7eb',
                color:      '#9ca3af',
                fontSize:   '13px',
              }}
            >
              <svg
                width="36"
                height="36"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.2"
              >
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path strokeLinecap="round" d="M8 6h8M8 10h8M8 14h5" />
              </svg>
              <span>
                Selecciona un <strong>contrato</strong>
                <br />
                y una <strong>fecha de inicio</strong>
              </span>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">

              {/* Tarjeta principal */}
              <div
                className="rounded p-4"
                style={{
                  background: `linear-gradient(135deg, ${activeColor} 0%, ${activeColor}cc 100%)`,
                  color:      '#fff',
                }}
              >
                <p
                  className="mb-1"
                  style={{
                    fontSize:      '11px',
                    fontWeight:    '600',
                    opacity:       0.8,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                  }}
                >
                  Monto prorrateado — primer mes
                </p>
                <p className="mb-1" style={{ fontSize: '38px', fontWeight: '800', lineHeight: 1.1 }}>
                  {fmt(calc.total)}
                </p>
                <p className="mb-0" style={{ fontSize: '12px', opacity: 0.8 }}>
                  {calc.days} días ×{' '}
                  {fmt(parseFloat((calc.rate / 30).toFixed(4)))}/día
                  {taxApplies ? ' · IVA incluido' : ' · Sin impuesto'}
                </p>
              </div>

              {/* Desglose */}
              <div
                className="rounded p-3"
                style={{ background: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '13px' }}
              >
                <div className="d-flex justify-content-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ color: '#6b7280' }}>Base imponible</span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{fmt(calc.base)}</span>
                </div>
                <div className="d-flex justify-content-between py-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ color: '#6b7280' }}>
                    {taxApplies ? `IVA (${taxPercent}%)` : 'Impuesto'}
                  </span>
                  <span style={{ fontWeight: '600', color: '#111827' }}>
                    {taxApplies ? fmt(calc.taxAmt) : 'No sujeto'}
                  </span>
                </div>
                <div className="d-flex justify-content-between pt-2">
                  <span style={{ fontWeight: '700', color: '#111827' }}>Total a cobrar</span>
                  <span style={{ fontWeight: '700', color: '#111827', fontSize: '15px' }}>{fmt(calc.total)}</span>
                </div>
              </div>

              {/* Nota próximo mes */}
              <div
                className="rounded p-3 d-flex gap-2 align-items-start"
                style={{
                  background: '#eff6ff',
                  border:     '1px solid #bfdbfe',
                  fontSize:   '13px',
                  color:      '#1e40af',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ flexShrink: 0, marginTop: '1px' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" d="M12 8v4m0 4h.01" />
                </svg>
                <span>
                  A partir del{' '}
                  <strong>
                    1 de {dayjs(startDate).add(1, 'month').format('MMMM [de] YYYY')}
                  </strong>
                  , el cobro automático será del{' '}
                  <strong>100% → {fmt(calc.fullMonth)}/mes</strong>
                  {taxApplies ? ' (IVA incluido)' : ''}.
                </span>
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default BillingCalculator
